import React from 'react';

import { useRouter } from 'next/router';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { GET_CASES_CLIENTS, GET_SUMMARY } from '@/api';
import { useAllActionRequired } from '@/hooks/queries/useAllActionRequired';
import {
	ChatPlaceholder,
	CompleteProfileBanner,
	SummaryCard,
	SummaryTypes,
} from '@/components/dashboard';
import { ActionRequiredCard } from '@/components/dashboard/cases/case-details/ActionRequiredCard';
import { dashboardApplicationsService } from '@/api/dashboardApplication';
import {
	useChatMessages,
	useShowChat,
	useSummaryDashboard,
	useUser,
} from '@/hooks';
import { Typography } from '@/components/common';
import { useEventSource } from '@/hooks/useEventSource';
import { useSession } from 'next-auth/react';
import useModal from '@/hooks/useModal';
import { ModalAlreadyActiveAccountContent } from '@/components/modals';
import clsx from 'clsx';
import { useIsSafari } from '@/hooks/useIsSafari';

const DashboardScreen: React.FC = () => {
	const { data: session } = useSession();
	const router = useRouter();
	const queryClient = useQueryClient();
	const { isSafari } = useIsSafari();

	const { pathname, query } = router;
	const { showUserActiveModal } = query;
	const { getSummaryOptions, getAmountByOption } = useSummaryDashboard();
	const {
		Modal: ModalAlreadyActiveAccount,
		show: showModalAlreadyActiveAccount,
	} = useModal();
	const { showChatBox, openShowChatBox } = useShowChat();
	const { addHelloMessage, chatMessages } = useChatMessages();
	const userId = session?.user.user.id;
	const { user } = useUser();

	const { data: stepNotification } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/action-resolved/subscribe`,
	});

	const firstParsed = stepNotification ? JSON.parse(stepNotification) : null;
	const parsedNotification = firstParsed ? JSON.parse(firstParsed.data) : null;

	const notUserId = parsedNotification?.clientId;
	const date = parsedNotification?.date;

	const initialStateSummary = getSummaryOptions();
	const [summaryOptions, setSummaryOptions] =
		React.useState(initialStateSummary);

	const { data: summary, refetch: refetchSummary } = useQuery({
		queryKey: [GET_SUMMARY],
		queryFn: () => dashboardApplicationsService.getSummary(),
	});

	const {
		data: allActionRequired,
		refetch: refetchAllActions,
		isError: isAllActionRequiredError,
	} = useAllActionRequired();

	// remove verify-error
	React.useEffect(() => {
		delete query['verify-error'];
		router.replace({
			pathname,
			query,
		});
	}, []);

	React.useEffect(() => {
		if (summary?.data) {
			const options = summaryOptions.map((option) => {
				return getAmountByOption(option, summary.data);
			});
			setSummaryOptions(options);
		}
	}, [summary]);

	React.useEffect(() => {
		if (userId === notUserId) {
			refetchSummary();
			refetchAllActions();
		}
	}, [date]);

	const handleRefetch = () => {
		refetchAllActions();
		queryClient.refetchQueries({
			queryKey: [GET_CASES_CLIENTS],
		});
	};

	React.useEffect(() => {
		if (showUserActiveModal) {
			showModalAlreadyActiveAccount();

			const updatedQuery = { ...query };
			delete updatedQuery.showUserActiveModal;

			router.replace(
				{
					pathname,
					query: updatedQuery,
				},
				undefined,
				{ shallow: true }
			);
		}
	}, [showUserActiveModal, query, pathname]);

	return (
		<div className="flex-1">
			<div className="space-y-8">
				<CompleteProfileBanner />
				<div
					className={clsx('flex gap-6 h-auto lg:h-[85vh]', {
						'h-auto md': isSafari,
					})}
				>
					<div className="gap-6 w-full xl:w-3/4 3xl:w-4/5 flex flex-col">
						<Typography type="bold-20" className="lg:hidden">
							Welcome
							{user?.firstname && (
								<>
									, <span className="text-primary">{user.firstname}</span>
								</>
							)}
						</Typography>
						<div
							className={clsx(
								'order-2 grid grid-cols-2 lg:order-1 lg:grid-cols-4 gap-4',
								{ 'mb-28 md:mb-0': isSafari }
							)}
						>
							{summaryOptions.map((opt, index) => (
								<SummaryCard
									classNameText="capitalize !text-xs h-auto lg:h-[32px] 2xl:h-auto"
									key={index}
									className="2xl:!pt-4 2xl:!pb-[18px]"
									type={opt.type as SummaryTypes}
									classNameContent="!gap-3"
									iconBgSize="!w-12 !h-12"
									iconSize="!w-6 !h-6"
									quantity={opt.amount}
								/>
							))}
						</div>
						<div className="order-1 !h-fit lg:order-2 lg:!h-[70vh] 2xl:!h-[73vh]">
							<ActionRequiredCard
								isDashboard
								className="lg:h-full lg:min-h-0"
								actionRequired={allActionRequired?.data}
								refetchActionRequired={handleRefetch}
								isError={isAllActionRequiredError}
								onRetry={refetchAllActions}
							/>
						</div>
					</div>
					<button
						type="button"
						onClick={() => {
							if (chatMessages.length === 0) {
								addHelloMessage();
							}
							openShowChatBox();
						}}
						className="hidden xl:block w-1/4 3xl:w-1/5"
						disabled={showChatBox}
					>
						{!showChatBox && <ChatPlaceholder />}
					</button>
				</div>
			</div>

			<ModalAlreadyActiveAccount
				classNameCardContainer={'w-full md:!w-[532px] !px-10 !py-8'}
				classNameContainerChildren={'!px-0'}
				withXButton
			>
				<ModalAlreadyActiveAccountContent />
			</ModalAlreadyActiveAccount>
		</div>
	);
};

export default DashboardScreen;
